import { useState } from 'react';

/**
 * A collapsible JSON view.
 *
 * Hand-rolled rather than pulled in: the one thing a generic viewer will not do
 * is stay honest about what the capture actually held, and this one is small
 * enough that keeping it is cheaper than configuring one.
 */

export interface JsonTreeProps {
  value: unknown;
  /** Nodes at or below this depth start open. */
  defaultExpandedDepth?: number;
}

export function JsonTree({ value, defaultExpandedDepth = 2 }: JsonTreeProps) {
  return (
    <div className="overflow-x-auto font-mono text-xs leading-relaxed">
      <Node value={value} depth={0} defaultExpandedDepth={defaultExpandedDepth} />
    </div>
  );
}

interface NodeProps {
  value: unknown;
  /** Absent for array items, which are addressed by position, not by key. */
  name?: string | undefined;
  depth: number;
  defaultExpandedDepth: number;
  /** Rendered after the value, e.g. the comma between siblings. */
  trailing?: string;
}

function Node({ value, name, depth, defaultExpandedDepth, trailing = '' }: NodeProps) {
  const container = isContainer(value);
  const [expanded, setExpanded] = useState(depth < defaultExpandedDepth);

  if (!container) {
    return (
      <div style={{ paddingLeft: depth * 12 }} className="whitespace-pre-wrap break-all">
        {name !== undefined && <Key name={name} />}
        <Scalar value={value} />
        <span className="text-slate-600">{trailing}</span>
      </div>
    );
  }

  const entries: [string, unknown][] = Array.isArray(value)
    ? value.map((item, index) => [String(index), item])
    : Object.entries(value as Record<string, unknown>);

  const [open, close] = Array.isArray(value) ? ['[', ']'] : ['{', '}'];

  if (entries.length === 0) {
    return (
      <div style={{ paddingLeft: depth * 12 }}>
        {name !== undefined && <Key name={name} />}
        <span className="text-slate-500">
          {open}
          {close}
        </span>
        <span className="text-slate-600">{trailing}</span>
      </div>
    );
  }

  return (
    <div>
      <div style={{ paddingLeft: depth * 12 }}>
        <button
          type="button"
          onClick={() => setExpanded((current) => !current)}
          aria-expanded={expanded}
          className="rounded text-left hover:bg-slate-800/60 focus:outline-none focus-visible:ring-1 focus-visible:ring-sky-400"
        >
          <span className="inline-block w-3 select-none text-slate-500">
            {expanded ? '▾' : '▸'}
          </span>
          {name !== undefined && <Key name={name} />}
          <span className="text-slate-500">{open}</span>
          {!expanded && (
            <span className="text-slate-600">
              … {entries.length} {entries.length === 1 ? 'elemento' : 'elementos'} {close}
              {trailing}
            </span>
          )}
        </button>
      </div>

      {expanded && (
        <>
          {entries.map(([key, item], index) => (
            <Node
              key={key}
              name={Array.isArray(value) ? undefined : key}
              value={item}
              depth={depth + 1}
              defaultExpandedDepth={defaultExpandedDepth}
              trailing={index < entries.length - 1 ? ',' : ''}
            />
          ))}
          <div style={{ paddingLeft: depth * 12 }} className="text-slate-500">
            <span className="inline-block w-3" />
            {close}
            <span className="text-slate-600">{trailing}</span>
          </div>
        </>
      )}
    </div>
  );
}

function Key({ name }: { name: string }) {
  return (
    <>
      <span className="text-sky-300">&quot;{name}&quot;</span>
      <span className="text-slate-500">: </span>
    </>
  );
}

function Scalar({ value }: { value: unknown }) {
  if (typeof value === 'string') {
    return <span className="text-emerald-300">&quot;{value}&quot;</span>;
  }
  if (typeof value === 'number') return <span className="text-amber-300">{String(value)}</span>;
  if (typeof value === 'boolean') return <span className="text-fuchsia-300">{String(value)}</span>;
  if (value === null) return <span className="text-slate-500">null</span>;
  return <span className="text-slate-300">{String(value)}</span>;
}

function isContainer(value: unknown): boolean {
  return typeof value === 'object' && value !== null;
}
